import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import MedicationInput from './medicationInput';
import MedicationNoteInput from './medicationNoteInput';
import AgyleDatePicker from '../../../../../components/ui/fields/agyleDatePicker';
import PrescriptionPatientWarnings from './prescriptionPatientWarnings';
import DrugSelectorComponent from './drugSelectorComponent';
import PrescriptionSectionHeader from './prescriptionSectionHeader';
import PrescriptionSubGroup from './prescriptionSubGroup';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: '10px 20px',
    },
    box: {
        padding: 10,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'column',
        gap: 5,
        width: '100%',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    divider: {
        margin: 10,
        background: theme.palette.divider,
    },
    borderRight: {
        borderRight: `1px solid ${theme.palette.divider}}`,
    },
}));

const DEFAULT_VALUE_FIELD = {
    value: null,
    code: null,
    comment: '',
};

const PERIOD_UNIT_OPTIONS = [
    {
        id: 'h',
        name: 'Hour',
    },
    {
        id: 'd',
        name: 'Day',
    },
    {
        id: 'w',
        name: 'Week',
    },
    {
        id: 'm',
        name: 'Month',
    },
];

const MedicationForm = forwardRef(({ open, addMedication, patient, value, disabled = false, onChange, pos = -1 }, ref) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const initForm = (value) => {
        return {
            id: uuidv4() + '',
            vmp: null,
            amp: null,
            ampp: null,
            startDate: {
                ...{ ...DEFAULT_VALUE_FIELD },
                value: moment(),
            },
            endDate: { ...DEFAULT_VALUE_FIELD },
            dose: { ...DEFAULT_VALUE_FIELD },
            form: { ...DEFAULT_VALUE_FIELD },
            frequency: { ...DEFAULT_VALUE_FIELD },
            period: { ...DEFAULT_VALUE_FIELD, value: 1 },
            periodUnit: { ...DEFAULT_VALUE_FIELD, value: 'd' },
            route: { ...DEFAULT_VALUE_FIELD },
            duration: { ...DEFAULT_VALUE_FIELD },
            quantity: { ...DEFAULT_VALUE_FIELD },
            instructions: { ...DEFAULT_VALUE_FIELD },
            unitDoseUnit: { ...DEFAULT_VALUE_FIELD },
            ...value,
        };
    };

    const [form, setForm] = useState(initForm(value || {}));
    const [inputsDisabled, setInputsDisabled] = useState(disabled || !form.vmp);

    const [error, setError] = useState(false);

    // Expose imperative API to parent components
    useImperativeHandle(
        ref,
        () => ({
            validateForm: () => valid(),
        }),
        [form],
    );

    useEffect(() => {
        if (open) {
            setForm(initForm(value || {}));
        }
        if (!value) {
            setError(false);
        }
    }, [value, open]);

    useEffect(() => {
        setInputsDisabled(disabled || !form?.vmp);
    }, [disabled, form?.vmp]);

    useEffect(() => {
        if (onChange && open) onChange(form);
    }, [form, open]);

    // main medicine has changed or removed
    useEffect(() => {
        if (!form?.vmp) {
            setForm({
                vmp: null,
                amp: null,
                ampp: null,
                startDate: {
                    ...{ ...DEFAULT_VALUE_FIELD },
                    value: moment(),
                },
                endDate: { ...DEFAULT_VALUE_FIELD },
                dose: { ...DEFAULT_VALUE_FIELD },
                form: { ...DEFAULT_VALUE_FIELD },
                frequency: { ...DEFAULT_VALUE_FIELD },
                period: { ...DEFAULT_VALUE_FIELD, value: 1 },
                periodUnit: { ...DEFAULT_VALUE_FIELD, value: 'd' },
                route: { ...DEFAULT_VALUE_FIELD },
                duration: { ...DEFAULT_VALUE_FIELD },
                quantity: { ...DEFAULT_VALUE_FIELD },
                instructions: { ...DEFAULT_VALUE_FIELD },
                unitDoseUnit: { ...DEFAULT_VALUE_FIELD },
            });
        }
    }, [form?.vmp]);

    const onInputChange = ({ field, value }) => {
        // Prevent changes if form is disabled
        if (disabled) return;

        const newVal = {
            ...form,
            [field]: {
                ...form[field],
                ...value,
            },
        };

        if (field === 'duration') {
            if (form?.startDate.value) {
                if (value.value > 0) {
                    newVal.endDate = {
                        ...newVal.endDate,
                        value: moment(form.startDate.value).add(value.value - 1, 'days'),
                    };
                } else {
                    newVal.endDate = {
                        ...newVal.endDate,
                        value: null,
                    };
                }
            }
        } else if (field === 'startDate') {
            if (form?.duration.value > 0 && value.value) {
                newVal.endDate = {
                    ...newVal.endDate,
                    value: moment(value.value).add(form.duration.value - 1, 'days'),
                };
            }
        } else if (field === 'endDate') {
            if (form?.startDate.value && value.value) {
                newVal.duration = {
                    ...newVal.startDate,
                    value: moment(value.value).diff(form.startDate.value, 'days') + 1,
                };
            }
        }

        setForm(newVal);
    };

    const valid = () => {
        const isValid =
            !!form?.vmp &&
            !_.isEmpty(form.dose.value) &&
            !_.isEmpty(form.form.value) &&
            !_.isEmpty(form.route.value) &&
            form.frequency.value > 0 &&
            !_.isEmpty(form.route.value) &&
            form.quantity.value > 0 &&
            !_.isEmpty(form.periodUnit.value) &&
            form.period.value > 0 &&
            form.duration.value > 0 &&
            !!form.startDate.value &&
            !!form.endDate.value;
        setError(!isValid);
        return isValid;
    };

    if (!open) return null;

    return (
        <>
            <Box className={classes.box}>
                <PrescriptionSectionHeader number={1} title={t('Medication Details') + (pos > -1 ? ` #${pos + 1}` : '')} />
            </Box>

            <Box className={classes.box}>
                <DrugSelectorComponent disabled={disabled} value={value} handleChange={setForm} error={error && !form.vmp} />
            </Box>

            <PrescriptionSectionHeader number={2} title={t('Dosage & Administration')} />

            <Stack spacing={2}>
                <PrescriptionSubGroup title={t('Dosage & Form')}>
                    <MedicationInput
                        title={t('Dosage')}
                        type="dose"
                        field="dose"
                        disabled={inputsDisabled}
                        value={form.dose}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.dose.value)}
                        xs={12}
                        sm={12}
                    />
                    <MedicationInput
                        title={t('Frequency')}
                        // type="frequency"
                        type="numericStepper"
                        field="frequency"
                        disabled={inputsDisabled}
                        value={form.frequency}
                        onChange={onInputChange}
                        error={error && form.frequency.value <= 0}
                        hideComment
                    />
                    <MedicationInput
                        title={t('Period')}
                        type="numericStepper"
                        field="period"
                        disabled={inputsDisabled}
                        value={form.period}
                        onChange={onInputChange}
                        error={error && form.period.value <= 0}
                        hideComment
                        minValue={0}
                        xs={6}
                        sm={3}
                    />
                    <MedicationInput
                        title={t('Period Unit')}
                        type="select"
                        field="periodUnit"
                        disabled={inputsDisabled}
                        value={form.periodUnit}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.periodUnit.value)}
                        hideComment
                        options={PERIOD_UNIT_OPTIONS}
                        clearButtonVisible={false}
                        xs={6}
                        sm={3}
                    />
                    <MedicationInput
                        title={t('Form')}
                        type="form"
                        field="form"
                        disabled={inputsDisabled}
                        value={form.form}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.form.value)}
                    />
                    {/* Route should be from a dictionary */}
                    <MedicationInput
                        title={t('Route')}
                        type="route"
                        field="route"
                        disabled={inputsDisabled}
                        value={form.route}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.route.value)}
                    />
                </PrescriptionSubGroup>

                <PrescriptionSubGroup title={t('Supply')}>
                    <MedicationInput
                        title={t('Quantity')}
                        type="numericStepper"
                        field="quantity"
                        disabled={inputsDisabled}
                        value={form.quantity}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.quantity.value)}
                        minValue={0}
                    />
                    <MedicationInput
                        title={t('Duration(in days)')}
                        type="numericStepper"
                        field="duration"
                        disabled={inputsDisabled}
                        value={form.duration}
                        onChange={onInputChange}
                        error={error && form.duration.value <= 0}
                        minValue={0}
                    />
                    <MedicationInput
                        title={t('Start Date')}
                        type="date"
                        field="startDate"
                        disabled={inputsDisabled}
                        value={form.startDate}
                        onChange={onInputChange}
                        error={error && _.isNil(form.startDate.value)}
                    />
                    <MedicationInput
                        title={t('End Date')}
                        type="date"
                        field="endDate"
                        disabled={inputsDisabled}
                        value={form.endDate}
                        onChange={onInputChange}
                        error={error && _.isNil(form.endDate.value)}
                    />
                </PrescriptionSubGroup>
            </Stack>

            <PrescriptionSectionHeader number={3} title={t('Additional Instructions')} />

            <MedicationNoteInput
                title={t('Medication Instructions')}
                type="instructions"
                disabled={inputsDisabled}
                value={form.instructions.value}
                onChange={(note) => onInputChange({ field: 'instructions', value: note })}
            />

            {/*<MedicationNoteInput title="Prescription Note" type="prescription" disabled={inputsDisabled} />*/}

            <PrescriptionSectionHeader number={4} title={t('Patient Warnings')} />

            <PrescriptionPatientWarnings patient={patient} />
        </>
    );
});

export { MedicationForm, DEFAULT_VALUE_FIELD };
